import { useState, useEffect, useRef } from "react";
import { useTranslator } from "@eo-locale/react";

import {
  FiSearch,
  InputGroup,
  Input,
  IconButton,
  calc
} from "@/storybook-components";
import PopoverAction from "../common/popover";
import useDebounce from "@/hooks/useDebounce";
import { highlightText } from "@/helpers/highlight-text";
import { scrollIntoView } from "@/lib";
import { useAppSelector } from "@/redux/store/hooks";

const SearchContent = ({
  open,
  highlightClass = "",
  locale,
  action,
  resetPage,
  ...searchParams
}): JSX.Element => {
  const { translate } = useTranslator();
  const [search, setSearch] = useState("");
  const [isMarkVisible, setIsMarkVisible] = useState(false);
  const { layout } = useAppSelector(
    (state) => state.brainstormers.brainstormer
  );

  useEffect(() => {
    if (search !== "" && search !== undefined && isMarkVisible) {
      highlightText(highlightClass, search);
    } else {
      highlightText(highlightClass, "");
    }
  }, [search, isMarkVisible, highlightClass]);

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const triggerDebounceRef = useRef(false);

  const handleChange = (event) => {
    setIsMarkVisible(false);
    setSearch(event.target.value);
  };

  const fetchSearchedList = async (text) => {
    action(
      searchParams.sortBy,
      searchParams.sortOrder,
      text,
      searchParams.categoryId
    );
    resetPage(1);
    setIsMarkVisible(true);

    if (layout === "BudgetAllocator") scrollIntoView(".ehq-mainContent");
    else scrollIntoView("#ideas-grid");
  };

  const debouncedValue = useDebounce(search, 1200);
  useEffect(() => {
    if (triggerDebounceRef.current) fetchSearchedList(debouncedValue);
    triggerDebounceRef.current = true;
  }, [debouncedValue]);

  return (
    <PopoverAction
      open={isSearchOpen}
      onOpenChange={setIsSearchOpen}
      content={
        <InputGroup
          w="full"
          data-testid="ideas-search-popover"
          startElement={<FiSearch color="gray.800" />}
          startElementProps={{
            pointerEvents: "none",
            h: { base: 12, md: 10 }
          }}
        >
          <Input
            placeholder={translate(`${locale}.search`)}
            borderColor="gray.500"
            color="gray.800"
            _focusVisible={{
              borderColor: "gray.500",
              borderWidth: "1px",
              boxShadow: "none"
            }}
            h={{ base: 12, md: 10 }}
            onChange={handleChange}
            aria-label={translate(`${locale}.search`)}
            data-testid="ideas-search-input"
          />
        </InputGroup>
      }
      triggerIcon={
        <IconButton
          aria-label={translate(`${locale}.search`)}
          icon={<FiSearch />}
          data-testid="ideas-search-trigger"
          variant="unstyled"
          w={{ base: 10, sm: 12, xl: 10 }}
          h={{ base: 10, sm: 12, xl: 10 }}
          p={2.5}
          justifyContent="center"
          alignItems="center"
          borderRadius="50%"
          bg={isSearchOpen ? "gray.300" : "white"}
          color="gray.900"
          boxShadow="lg"
          _hover={
            isSearchOpen
              ? { bg: "gray.300", color: "gray.900" }
              : { bg: "white", color: "gray.900" }
          }
          _focus={{
            bg: "white",
            color: "gray.900"
          }}
        />
      }
      bg="transparent"
      borderColor="transparent"
      maxW={{ base: "100%", md: 72 }}
      w={calc.subtract("100vw", "16px")}
      h={{ base: 12, md: 10 }}
      left={2}
    />
  );
};

export default SearchContent;
